import React, { Component } from 'react';
import { View, StyleSheet } from 'react-native';

import {
  Button,
  Card,
  FileUpload as FileUploadView,
  FormField,
  Page,
  RadioGroup,
  RadioItem,
  Text,
  TextInputWithError,
} from '../uiold';
import { showError } from '../App/AppAlert';
import { showDialog } from '../App/AppDialog';

import FileUpload from '../../support/FileUpload';
import { UPLOAD_MAX_SIZE } from '../../support/shared/constants/Constants';
import Editor, {
  EditorContent,
  createEditorContent,
} from '../../core-ui/Editor/StatefulEditor';

import { Attachment } from '../../types/shared/FileUpload';
import { AttachmentPreview } from '../../support/AttachmentPreview';
import { getFileURL, toFile } from '../../support/helpers/Files';
import { GetPortalPages_myAccount_currentSeason_vendorPortalPages as VendorPortalPage } from './__generated__/GetPortalPages';
import { useMutation } from '@apollo/client';
import {
  CREATE_VENDOR_PORTAL_PAGE,
  DELETE_VENDOR_PORTAL_PAGE,
  GET_PORTAL_PAGES,
  UPDATE_VENDOR_PORTAL_PAGE,
} from './queries';
import { LoadingIndicator } from '../../core-ui';
import { UpdateVendorPortalPageInput } from '../../types/__generated__/globalTypes';
import { useSeason } from '../SeasonState';
import { useAuthState } from '../AuthState';

type Props = {
  vendorPortalPage?: VendorPortalPage;
  canEdit: boolean;
  onClose: () => void;
  isSaving: boolean;
  onSave: (data: UpdateVendorPortalPageInput) => void;
  onDelete: () => void;
};

type State = {
  updates: {
    title: VendorPortalPage['title'];
    content: EditorContent;
    // TODO: Combine this with fileUploads below
    attachments: Array<AttachmentPreview>;
    isPublic: VendorPortalPage['isPublic'];
  };
  errors: {
    title: string | null;
    content: string | null;
    attachments: string | null;
    isPublic: string | null;
  };
  // This is only the in-progress uploads
  // TODO: Combine with attachments above
  fileUploads: Map<string, FileUpload>;
};

class VendorPortalPageEditorView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { vendorPortalPage } = props;
    this.state = {
      updates: {
        title: vendorPortalPage?.title ?? '',
        content: createEditorContent(vendorPortalPage?.content ?? ''),
        attachments: toAttachmentPreviewList(
          vendorPortalPage?.attachments ?? [],
        ),
        isPublic: vendorPortalPage?.isPublic ?? false,
      },
      errors: {
        title: null,
        content: null,
        attachments: null,
        isPublic: null,
      },
      fileUploads: new Map(),
    };
  }

  componentWillUnmount() {
    for (let [, fileUpload] of this.state.fileUploads) {
      this._abortUpload(fileUpload);
    }
  }

  render() {
    const { vendorPortalPage, canEdit, isSaving } = this.props;
    const { updates, errors } = this.state;
    const { title, content, attachments, isPublic } = updates;
    const pageTitle = vendorPortalPage ? t('Edit Page') : t('Create New Page');
    if (isSaving) {
      return (
        <Page>
          <Page.Header title={pageTitle} />
          <Card style={styles.editorCard}>
            <LoadingIndicator />
          </Card>
        </Page>
      );
    }
    return (
      <Page>
        <Page.Header title={pageTitle}>
          <Button title={t('Back to List')} onPress={this._onBackPress} />
          {canEdit && this._renderHeaderButtons()}
        </Page.Header>
        <Card style={styles.editorCard}>
          <FormField
            render={(id) => (
              <View style={styles.formField}>
                <Text
                  type="regular"
                  accessibilityRole="label"
                  htmlFor={id}
                  style={styles.title}
                >
                  {t('Page Name:')}
                </Text>
                <TextInputWithError
                  disabled={!canEdit}
                  id={id}
                  value={title}
                  onChangeText={this._onTitleChange}
                  skipFirstCheck
                  checkError={() => !errors.title}
                  errorMessage={errors.title}
                />
              </View>
            )}
          />
          <FormField
            render={(id) => (
              <View style={styles.formField}>
                <Text
                  type="regular"
                  accessibilityRole="label"
                  htmlFor={id}
                  style={styles.title}
                >
                  {t('Page Content:')}
                </Text>
                <Editor
                  height={280}
                  initialContent={content}
                  onChange={(content) => {
                    this.setState(({ updates }) => ({
                      updates: { ...updates, content },
                    }));
                  }}
                  readOnly={!canEdit}
                />
              </View>
            )}
          />
          <View style={styles.formField}>
            <Text type="regular" accessibilityRole="label" style={styles.title}>
              {t('Visibility:')}
            </Text>
            <RadioGroup
              selectedValue={isPublic}
              onChange={(value) => {
                if (!canEdit) {
                  return;
                }
                this.setState(({ updates }) => ({
                  updates: { ...updates, isPublic: String(value) === 'true' },
                }));
              }}
              style={{ flexDirection: 'row' }}
            >
              <RadioItem title={t('Public')} value={true} />
              <RadioItem title={t('Private')} value={false} />
            </RadioGroup>
          </View>
          <View style={styles.formField}>
            <Text type="regular" style={styles.title}>
              {t('Attachments:')}
            </Text>
            {/* TODO: Replace/rewrite this component */}
            <FileUploadView
              files={attachments}
              acceptMultipleFiles={true}
              onFileDropped={this._onFileDropped}
              onFileChange={this._onFileChange}
              onFileDelete={this._onFileDelete}
            />
          </View>
        </Card>
      </Page>
    );
  }

  _renderHeaderButtons = () => {
    const { vendorPortalPage } = this.props;
    if (vendorPortalPage?.status === 'Published') {
      return (
        <>
          <Button title={t('Delete')} onPress={this._onDelete} />
          <Button
            isPrimary
            title={t('Save')}
            onPress={() => this._onSavePage('Published')}
          />
        </>
      );
    }
    return (
      <>
        {vendorPortalPage ? (
          <Button title={t('Delete')} onPress={this._onDelete} />
        ) : null}
        <Button
          title={t('Save as Draft')}
          onPress={() => this._onSavePage('Draft')}
        />
        <Button
          isPrimary
          title={t('Publish')}
          onPress={() => this._onSavePage('Published')}
        />
      </>
    );
  };

  _onFileChange = (attachments: Array<AttachmentPreview>) => {
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments },
    }));
  };

  _onFileDelete = (
    attachments: Array<AttachmentPreview>,
    deletedFile: AttachmentPreview,
  ) => {
    let fileUploads = new Map(this.state.fileUploads);
    let fileUpload = fileUploads.get(deletedFile.id);
    if (fileUpload) {
      this._abortUpload(fileUpload);
      fileUploads.delete(deletedFile.id);
    }
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments },
      fileUploads,
    }));
  };

  _onFileDropped = (added: Array<AttachmentPreview>) => {
    let { attachments } = this.state.updates;
    let fileUploads = new Map(this.state.fileUploads);
    let newAttachments: Array<AttachmentPreview> = [];
    for (let attachment of added) {
      let { id, file } = attachment;
      if (file && this._isFileSizeUnderLimit(file)) {
        let fileUpload = new FileUpload('/api/attachments', file);
        fileUploads.set(id, fileUpload);
        fileUpload.on('progress', (loaded, total) => {
          this._updateProgress((loaded / total) * 100, id);
        });
        fileUpload.on('complete', (error, response) => {
          if (error) {
            this._onUploadError(id, error.message);
            return;
          }
          const { status, body } = response;
          if (status !== 200) {
            this._onUploadError(id, `Upload failed (status ${status})`);
            return;
          }
          this._onUploadSuccess(id, body ?? {});
        });
        fileUpload.send();
        newAttachments.push(attachment);
      }
    }
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments: [...attachments, ...newAttachments] },
      fileUploads,
    }));
  };

  _isFileSizeUnderLimit = (file: File) => {
    if (file.size > UPLOAD_MAX_SIZE) {
      showError(
        t('Error: File {name} exceeds maximum size.', { name: file.name }),
      );
      return false;
    }
    return true;
  };

  _onUploadError = (id: string, error: string) => {
    let newAttachments = this.state.updates.attachments.filter(
      (attachment) => attachment.id !== id,
    );
    let fileUploads = new Map(this.state.fileUploads);
    fileUploads.delete(id);
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments: newAttachments },
      fileUploads,
    }));
    // TODO: Attach an individual error to each file upload
    showError(t('Error uploading file: {error}', { error }));
  };

  _onUploadSuccess = (id: string, data: ObjectOf<unknown>) => {
    let { updates } = this.state;
    let newAttachments = updates.attachments.map((attachment) =>
      attachment.id === id
        ? { ...attachment, id: String(data.id) }
        : attachment,
    );
    let fileUploads = new Map(this.state.fileUploads);
    fileUploads.delete(id);
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments: newAttachments },
      fileUploads,
    }));
  };

  _updateProgress = (progress: number, id: string) => {
    let { updates } = this.state;
    let newAttachments = updates.attachments.map((attachment) =>
      attachment.id === id
        ? { ...attachment, uploadProgress: progress }
        : attachment,
    );
    this.setState(({ updates }) => ({
      updates: { ...updates, attachments: newAttachments },
    }));
  };

  _abortUpload = (upload: FileUpload) => {
    upload.abort();
  };

  _onTitleChange = (title: string) => {
    const error: string | null = validateTitle(title);
    this.setState(({ updates, errors }) => {
      return {
        updates: { ...updates, title },
        errors: { ...errors, title: error },
      };
    });
  };

  _isDirty = () => {
    const { vendorPortalPage } = this.props;
    const { updates } = this.state;
    if (!vendorPortalPage) {
      return (
        updates.title !== '' ||
        updates.content.toString() !== '' ||
        updates.attachments.length > 0
      );
    }
    if (
      updates.title !== vendorPortalPage.title ||
      updates.content.toString() !== vendorPortalPage.content ||
      updates.isPublic !== vendorPortalPage.isPublic
    ) {
      return true;
    }
    if (updates.attachments.length !== vendorPortalPage.attachments.length) {
      return true;
    }
    return (
      updates.attachments.map((a) => a.id).join() !==
      vendorPortalPage.attachments.map((a) => a.id).join()
    );
  };

  _onBackPress = () => {
    if (!this._isDirty()) {
      this.props.onClose();
      return;
    }
    const CANCEL = t('Never mind');
    const DISCARD = t('Discard Changes');
    showDialog(
      t('Discard Changes?'),
      t('Do you want to discard your changes?'),
      [CANCEL, DISCARD],
      (value) => {
        if (value === DISCARD) {
          this.props.onClose();
        }
      },
    );
  };

  _onDelete = () => {
    const CANCEL = t('Cancel');
    const DELETE = t('Delete');
    showDialog(
      t('Delete Page?'),
      t('Are you sure you want to permanently delete this page?'),
      [CANCEL, DELETE],
      (value) => {
        if (value === DELETE) {
          this.props.onDelete();
        }
      },
    );
  };

  _onSavePage = (newStatus: VendorPortalPage['status']) => {
    const { title, content, attachments, isPublic } = this.state.updates;
    const titleError = validateTitle(title);
    if (titleError != null) {
      showError(titleError);
      return;
    }
    this.props.onSave({
      title,
      content: content.toString(),
      attachments: fromAttachmentPreviewList(attachments),
      isPublic,
      status: newStatus,
    });
  };
}

function validateTitle(title: string): string | null {
  if (title.length > 50) {
    return t('Title cannot exceed 50 characters');
  } else if (title.trim().length === 0) {
    return t('Title cannot be empty');
  }
  return null;
}

function toAttachmentPreviewList(attachments: Array<Attachment>) {
  return attachments.map(
    (attachment): AttachmentPreview => {
      // I'm pretty sure this preview URL won't actually be used.
      const preview = getFileURL(toFile(attachment), { size: 'thumbnail' });
      return { ...attachment, preview, uploadProgress: 100 };
    },
  );
}

function fromAttachmentPreviewList(attachments: Array<AttachmentPreview>) {
  return attachments.map(
    (attachment): Attachment => ({
      id: attachment.id,
      fileType: attachment.fileType,
      fileName: attachment.fileName,
      size: attachment.size,
    }),
  );
}

let styles = StyleSheet.create({
  title: {
    marginBottom: 10,
  },
  formField: {
    padding: 20,
  },
  editorCard: {
    marginBottom: 20,
  },
});

export default function VendorPortalPageEditor(props: {
  vendorPortalPage?: VendorPortalPage;
  onClose: () => void;
  canEdit: boolean;
}) {
  const { vendorPortalPage, onClose, canEdit } = props;
  const { account } = useAuthState();
  const { season } = useSeason();
  const seasonId = season ? season.id : account.currentSeason;
  const [createPage, { loading: isCreating }] = useMutation(
    CREATE_VENDOR_PORTAL_PAGE,
    {
      onCompleted: () => onClose(),
      onError: (error) => showError(String(error)),
      refetchQueries: [GET_PORTAL_PAGES],
    },
  );
  const [updatePage, { loading: isUpdating }] = useMutation(
    UPDATE_VENDOR_PORTAL_PAGE,
    {
      onCompleted: () => onClose(),
      onError: (error) => showError(String(error)),
      refetchQueries: [GET_PORTAL_PAGES],
    },
  );
  const [deletePage, { loading: isDeleting }] = useMutation(
    DELETE_VENDOR_PORTAL_PAGE,
    {
      onCompleted: () => onClose(),
      onError: (error) => showError(String(error)),
      refetchQueries: [GET_PORTAL_PAGES],
    },
  );
  return (
    <VendorPortalPageEditorView
      vendorPortalPage={vendorPortalPage}
      canEdit={canEdit}
      onClose={onClose}
      isSaving={isCreating || isUpdating || isDeleting}
      onSave={(data) => {
        if (vendorPortalPage) {
          void updatePage({
            variables: {
              id: vendorPortalPage.id,
              updates: data,
            },
          });
        } else {
          void createPage({
            variables: {
              seasonId,
              data,
            },
          });
        }
      }}
      onDelete={() => {
        void deletePage({
          variables: {
            id: vendorPortalPage?.id ?? '',
          },
        });
      }}
    />
  );
}
